/**
 * Typed fetch wrapper for the Dissregarded API.
 *
 * - Always `credentials: "include"` so the .dissregarded.app session cookie flows
 * - On the first mutating request, auto-fetches a CSRF token from /api/csrf
 *   and echoes the cookie value in `X-Csrf-Token`
 * - Throws `ApiError` on non-2xx so callers can `catch` and surface a message
 */
import { env } from "./env.ts";

let csrfToken: string | null = null;

function readCsrfCookie(): string | null {
  const m = document.cookie.match(/(?:^|;\s*)ds_csrf=([^;]+)/);
  return m?.[1] ?? null;
}

async function ensureCsrf(): Promise<string | null> {
  if (csrfToken) return csrfToken;
  const fromCookie = readCsrfCookie();
  if (fromCookie) {
    csrfToken = fromCookie;
    return csrfToken;
  }
  await fetch(`${env.API_URL}/csrf`, { credentials: "include" });
  csrfToken = readCsrfCookie();
  return csrfToken;
}

export class ApiError extends Error {
  constructor(message: string, public status: number, public body?: unknown) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? "GET").toUpperCase();
  const headers = new Headers(init.headers);
  headers.set("accept", "application/json");
  if (init.body && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }

  if (method !== "GET" && method !== "HEAD") {
    const token = await ensureCsrf();
    if (token) headers.set("x-csrf-token", token);
  }

  const url = path.startsWith("http") ? path : `${env.API_URL}${path}`;
  const res = await fetch(url, { ...init, headers, credentials: "include" });

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const body = text ? safeParse(text) : null;
  if (!res.ok) {
    throw new ApiError(
      (body as { error?: string })?.error ?? res.statusText,
      res.status,
      body
    );
  }
  return body as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "POST", body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(body) }),
  del: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};
